/* istanbul ignore file */
import { useSelector } from 'react-redux';
import { ApplicationSubmissionStatus } from '@common/entities';
import { useQueryClient } from '@tanstack/react-query';
import { RootState } from 'state/store';

import { applicationComplete } from 'services';
import { ApplicationStatusResponse } from 'services/applicant';

import { submissionStatusQueryKey } from './useSubmissionStatus';

interface PaymentInfoProp {
  enabled: boolean;
  processed: boolean;
}
export function useCompleteApplication() {
  const { applicant, activeResponse } = useSelector((state: RootState) => state.application);
  const queryClient = useQueryClient();

  const { email, propertyId } = applicant;
  const { applicationId, responseId, versionId } = activeResponse;

  function validateRequestData() {
    if (!applicationId) throw new Error('Application Id is not available.');
    if (!responseId) throw new Error('Response Id is not available.');
    if (!versionId) throw new Error('Version Id is not available.');
    if (!email) throw new Error('Email Id is not available.');
    if (!propertyId) throw new Error('Property Id is not available.');
  }

  async function completeApplication(
    digitalSignature: string,
    paymentInfo: PaymentInfoProp,
  ): Promise<string> {
    validateRequestData();
    const ipAddress = '174.253.83.192';

    if (responseId) {
      const queryKey = submissionStatusQueryKey(responseId);
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.removeQueries({ queryKey, exact: true });
    }

    const response = await applicationComplete({
      propertyId,
      applicationId,
      versionId,
      emailAddress: email,
      payment: paymentInfo,
      digitalSignature,
      ipAddress,
      responseId: responseId as string,
    });
    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        'Something went wrong with submitting your application. Please try again later.',
      );
    }
    queryClient.setQueryData<ApplicationStatusResponse[]>(
      ['applicant-applications'],
      (applications) =>
        applications?.map((application) =>
          application.responseId === responseId
            ? { ...application, submissionStatus: ApplicationSubmissionStatus.Submitting }
            : application,
        ),
    );
    return responseId;
  }

  return completeApplication;
}
